import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import CustomSelect from "../CustomSelect";
import api from "../api";

const yesNoOptions = [
  { value: true, label: "Si" },
  { value: false, label: "No" },
];

const enumOptions = {
  empatia: [
    { value: "Bassa", label: "Bassa" },
    { value: "Medio-bassa", label: "Medio-bassa" },
    { value: "Media", label: "Media" },
    { value: "Medio-alta", label: "Medio-alta" },
    { value: "Alta", label: "Alta" },
  ],
  inglese: [
    { value: "Basso", label: "Basso" },
    { value: "Medio", label: "Medio" },
    { value: "Alto", label: "Alto" },
  ],
  adeguatoInsegnamento: [
    { value: "Non adatto", label: "Non adatto" },
    { value: "Basso", label: "Basso" },
    { value: "Medio-basso", label: "Medio-basso" },
    { value: "Medio", label: "Medio" },
    { value: "Medio-alto", label: "Medio-alto" },
    { value: "Alto", label: "Alto" },
  ],
};

const EnumField = ({
  field,
  label,
  options,
  required = false,
  form,
  handleChange,
  editMode,
}) => (
  <div className="info-item">
    <label className="info-label">{label}:</label>
    <div className="info-value">
      {editMode ? (
        <CustomSelect
          options={options}
          value={options.find((opt) => opt.value === form[field]) || null}
          onChange={(selected) => handleChange(field, selected?.value ?? null)}
          isClearable={!required}
          placeholder={`Seleziona ${label.toLowerCase()}`}
          className="modern-select"
        />
      ) : (
        <span className="value-text">
          {options.find((opt) => opt.value === form[field])?.label || "—"}
        </span>
      )}
    </div>
  </div>
);

const EditIntervista = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [intervista, setIntervista] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchIntervista();
    }
  }, [navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    return `${String(date.getDate()).padStart(2, "0")}.${String(
      date.getMonth() + 1
    ).padStart(2, "0")}.${date.getFullYear()}`;
  };

  const fetchIntervista = async () => {
    try {
      const res = await api.get(`/interviste/${id}`);
      const data = res.data;
      setIntervista(data);
      setForm({
        dataIntervista: data.dataIntervista?.slice(0, 10) || "",
        parlantina: data.parlantina ?? null,
        timidezza: data.timidezza ?? null,
        dinamicità: data.dinamicità ?? null,
        empatia: data.empatia || "",
        inglese: data.inglese || "",
        adeguatoInsegnamento: data.adeguatoInsegnamento || "",
        automunito: data.automunito ?? null,
        piva: data.piva ?? null,
        luogoLavorativoDesiderato: data.luogoLavorativoDesiderato || "",
        annotazioneInterna: data.annotazioneInterna || "",
      });
    } catch (error) {
      console.log(error);

      toast.error(error?.response?.data?.message, { transition: Bounce });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        dataIntervista: form.dataIntervista
          ? new Date(form.dataIntervista).toISOString()
          : null,
        trainerCodiceFiscale: intervista.trainer.codiceFiscale,
      };
      const res = await api.put(`/interviste/${id}`, payload);
      if (res.status === 200) {
        toast.success("Intervista aggiornata con successo!", {
          transition: Bounce,
        });
        setEditMode(false);
        fetchIntervista();
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.title ||
        "Errore durante il salvataggio!";
      toast.error(errorMessage, { transition: Bounce });
    }
  };

  if (loading) {
    return (
      <div className="overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <div className="page-header">
        <div className="page-title">
          <i className="fa-solid fa-edit"></i>
          Modifica Intervista
        </div>
        <div className="controls">
          <button onClick={() => navigate("/interviste")} className="back-btn">
            <i className="fa-solid fa-arrow-left"></i>
            Torna alle Interviste
          </button>
        </div>
      </div>

      <div className="page-content interviste-edit">
        <div className="details-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fa-solid fa-microphone"></i>
              Dettagli Intervista #{intervista.id}
            </div>
            {!editMode && (
              <div className="card-actions">
                <button className="edit-btn" onClick={() => setEditMode(true)}>
                  <i className="fa-solid fa-edit"></i>
                  Modifica
                </button>
              </div>
            )}
          </div>

          <div className="card-content">
            {/* Informazioni Generali */}
            <div className="info-section">
              <div className="section-header">Informazioni Generali</div>
              <div className="info-grid">
                <div className="info-item">
                  <label className="info-label">Protocollo:</label>
                  <span className="info-value">
                    {intervista.nprotocolloIntervista}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Trainer ID:</label>
                  <span className="info-value">{intervista.trainer?.id}</span>
                </div>
                <div className="info-item">
                  <label className="info-label">Autore:</label>
                  <span className="info-value">{intervista.autore}</span>
                </div>
                <div className="info-item">
                  <label className="info-label">Data Intervista:</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={form.dataIntervista}
                      onChange={(e) =>
                        handleChange("dataIntervista", e.target.value)
                      }
                      className="modern-input"
                    />
                  ) : (
                    <span className="info-value">
                      {formatDate(form.dataIntervista)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Dati Docente */}
            <div className="info-section">
              <div className="section-header">Dati Docente</div>
              <div className="info-grid">
                <div className="info-item">
                  <label className="info-label">Nome:</label>
                  <span className="info-value">{intervista.trainer?.nome}</span>
                </div>
                <div className="info-item">
                  <label className="info-label">Cognome:</label>
                  <span className="info-value">
                    {intervista.trainer?.cognome}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Email:</label>
                  <span className="info-value">
                    {intervista.trainer?.email}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Codice Fiscale:</label>
                  <span className="info-value">
                    {intervista.trainer?.codiceFiscale}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Stato Occupazionale:</label>
                  <span className="info-value">
                    {intervista.trainer?.statoOccupazionale}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Livello Inglese:</label>
                  <span className="info-value">
                    {intervista.trainer?.livelloInglese}
                  </span>
                </div>
                <div className="info-item">
                  <label className="info-label">Livello Informatica:</label>
                  <span className="info-value">
                    {intervista.trainer?.livelloInformatica}
                  </span>
                </div>
              </div>
            </div>

            {/* Dati Intervista */}
            <div className="info-section">
              <div className="section-header">Dati Intervista</div>
              <div className="info-grid">
                <EnumField
                  field="parlantina"
                  label="Parlantina"
                  options={yesNoOptions}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                />

                <EnumField
                  field="timidezza"
                  label="Timidezza"
                  options={yesNoOptions}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                />
                <EnumField
                  field="dinamicità"
                  label="Dinamicità"
                  options={yesNoOptions}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                />
                <EnumField
                  field="automunito"
                  label="Automunito"
                  options={yesNoOptions}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                />
                <EnumField
                  field="piva"
                  label="P.IVA"
                  options={yesNoOptions}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                />
                <EnumField
                  field="empatia"
                  label="Empatia"
                  options={enumOptions.empatia}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                  required
                />
                <EnumField
                  field="inglese"
                  label="Inglese"
                  options={enumOptions.inglese}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                  required
                />
                <EnumField
                  field="adeguatoInsegnamento"
                  label="Adeguatezza Insegnamento"
                  options={enumOptions.adeguatoInsegnamento}
                  form={form}
                  handleChange={handleChange}
                  editMode={editMode}
                  required
                />

                <div className="info-item">
                  <label className="info-label">Punteggio Intervista:</label>
                  <span className="info-value">
                    {intervista.punteggioIntervista}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="section-header">
                <i className="fa-solid fa-clipboard"></i>
                Dettagli Aggiuntivi
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label className="info-label">
                    Luogo Lavorativo Desiderato:
                  </label>
                  <div className="info-value">
                    {editMode ? (
                      <input
                        type="text"
                        value={form.luogoLavorativoDesiderato}
                        onChange={(e) =>
                          handleChange(
                            "luogoLavorativoDesiderato",
                            e.target.value
                          )
                        }
                        className="modern-input"
                      />
                    ) : (
                      <span className="value-text">
                        {form.luogoLavorativoDesiderato || "—"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="info-item full-width">
                  <label className="info-label">Annotazione Interna:</label>
                  <div className="info-value">
                    {editMode ? (
                      <textarea
                        value={form.annotazioneInterna}
                        onChange={(e) =>
                          handleChange("annotazioneInterna", e.target.value)
                        }
                        className="modern-textarea"
                        rows={4}
                      />
                    ) : (
                      <span className="value-text">
                        {form.annotazioneInterna || "—"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {editMode && (
              <div className="edit-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setEditMode(false)}
                >
                  <i className="fa-solid fa-times"></i> Annulla
                </button>
                <button className="btn-primary" onClick={handleSave}>
                  <i className="fa-solid fa-save"></i> Salva Modifiche
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditIntervista;
